import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppSettings, DEFAULT_SECTIONS } from '../context/AppSettingsContext';
import axios from 'axios';
import { Home, Heart, Stethoscope, Scale, Newspaper, MessageCircle, User, LogOut, Menu, X, Shield, MapPin, Search, Users2, HelpCircle, Star, BookOpen, Globe, Crown, ChevronDown } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { LotusLogo } from './LotusLogo';
import { getAvatarImage } from './avatarSystem';
import LanguageSwitch from './world/LanguageSwitch';
import { nazevSekce } from '../lib/nazvySekci';
import { useJazyk } from '../context/LanguageContext';
import { API } from '../lib/api';

// Icons per content type for autocomplete dropdown
const TYPE_ICONS = {
  posts: Newspaper,
  users: Users2,
  specialists: Stethoscope,
  services: Heart,
  articles: Scale,
  questions: HelpCircle,
  reviews: Star,
};
// Druh výsledku ve vyhledávání; popisky jsou ve slovníku pod `typ.*`.
const TYPE_KEYS = ['posts', 'users', 'specialists', 'services', 'articles', 'questions', 'reviews'];
function getSuggestionTitle(type, item) {
  return item.title || item.username || item.name || [item.offer, item.need].filter(Boolean).join(' ↔ ') || '';
}
function getSuggestionLink(type, item) {
  if (type === 'posts') return `/news/${item.id}`;
  if (type === 'users') return `/users/${item.id}`;
  if (type === 'specialists') return '/specialists';
  if (type === 'services') return '/support';
  if (type === 'questions') return '/legal';
  if (type === 'reviews') return '/specialists';
  return '/legal';
}

// Base nav items — labels are overridden by admin section settings
// Co zůstává v liště samostatně. Zbytek se schoval pod T-World, aby lišta
// nebyla řada osmi položek, ve které se nikdo nevyzná.
const BASE_NAV_ITEMS = [
  { path: '/', icon: Home, defaultLabel: 'Domů', klic: 'nav.domu', sectionKey: null },
  { path: '/news', icon: Newspaper, defaultLabel: 'Aktuality', klic: 'nav.news', sectionKey: 'news' },
  { path: '/zkusenosti', icon: BookOpen, defaultLabel: 'Zkušenosti komunity', klic: 'nav.stories', sectionKey: 'stories' },
];

// T-World zastřešuje všechno, co se váže k místu: mapu světa, odborníky,
// podporu, právní poradnu, soutěže i okolí. Jsou to různé stránky, ale ještě
// pořád jedna otázka: co je k dispozici tam, kde jsem nebo kam jedu.
const TWORLD_ITEMS = [
  { path: '/world', icon: Globe, defaultLabel: 'Mapa světa', klic: 'nav.mapaSveta', sectionKey: null },
  { path: '/support', icon: Heart, defaultLabel: 'Vzájemná podpora', klic: 'nav.support', sectionKey: 'support' },
  { path: '/specialists', icon: Stethoscope, defaultLabel: 'Trans-friendly odborníci', klic: 'nav.specialists', sectionKey: 'specialists' },
  { path: '/legal', icon: Scale, defaultLabel: 'Právní poradna', klic: 'nav.legal', sectionKey: 'legal' },
  { path: '/pageants', icon: Crown, defaultLabel: 'Soutěže', klic: 'nav.pageants', sectionKey: null },
  { path: '/nearby', icon: MapPin, defaultLabel: 'V mém okolí', klic: 'nav.nearby', sectionKey: 'nearby' },
];

export const Navigation = () => {
  const { user, logout, isAdmin, isAuthenticated } = useAuth();
  const { sections } = useAppSettings();
  const { t } = useJazyk();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileToggleRef = useRef(null);

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setSuggestions(null); return; }
    try {
      const r = await axios.get(`${API}/search`, { params: { q: q.trim(), limit: 3 } });
      setSuggestions(r.data.results);
    } catch { setSuggestions(null); }
  }, []);

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 280);
    // Do NOT close the panel based on input length — panel stays open until click-away or submit
  };

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    if (searchQuery.trim().length >= 2) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setMobileSearchOpen(false);
      setSearchQuery('');
      setSuggestions(null);
    }
  };

  const hasSuggestions = suggestions && Object.values(suggestions).some(a => a?.length > 0);

  // Viditelnost i názvy se řídí nastavením z administrace – stejně pro lištu
  // i pro položky pod T-World, ať se schovaná sekce nevynořila jinudy.
  const pripravSkupinu = (polozky) => polozky
    .filter((item) => {
      if (!item.sectionKey) return true;
      return sections?.[item.sectionKey]?.visible !== false;
    })
    .map((item) => {
      // Server názvy sekcí předvyplňuje česky, takže „vlastní název" má
      // formálně každá sekce. Za skutečné přejmenování se počítá jen text,
      // který se od výchozích znění liší — rozhoduje o tom `nazevSekce`,
      // aby platilo stejné pravidlo tady, v nadpisech i na dlaždicích.
      const nastaveny = item.sectionKey ? sections?.[item.sectionKey]?.title : '';
      return {
        ...item,
        label: item.klic
          ? nazevSekce(t, nastaveny, item.klic, DEFAULT_SECTIONS[item.sectionKey]?.title)
          : (nastaveny || item.defaultLabel),
        order: item.sectionKey ? (sections?.[item.sectionKey]?.order ?? 99) : 0,
      };
    })
    .sort((a, b) => a.order - b.order);

  const navItems = pripravSkupinu(BASE_NAV_ITEMS);
  const tworldItems = pripravSkupinu(TWORLD_ITEMS);
  const vTworld = tworldItems.some((i) => location.pathname.startsWith(i.path) && i.path !== '/');

  useEffect(() => {
    if (isAuthenticated) {
      fetchUnread();
      const interval = setInterval(fetchUnread, 15000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (location.pathname === '/messages') {
      setTimeout(fetchUnread, 1000);
    }
  }, [location.pathname]);

  const fetchUnread = async () => {
    try {
      const res = await axios.get(`${API}/messages/unread-count`);
      setUnreadCount(res.data.count);
    } catch {}
  };

  const handleSearch = useCallback(async (q) => {
    handleSearchChange(q);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) { setSearchOpen(false); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close mobile menu on outside tap, scroll, or route change
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handler = (e) => {
      if (
        mobileMenuRef.current && !mobileMenuRef.current.contains(e.target) &&
        mobileToggleRef.current && !mobileToggleRef.current.contains(e.target)
      ) {
        setMobileMenuOpen(false);
      }
    };
    const scrollHandler = () => setMobileMenuOpen(false);
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    window.addEventListener('scroll', scrollHandler, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [mobileMenuOpen]);

  // Close mobile menu on navigation
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  if (!isAuthenticated) return null;

  const handleLogout = () => { logout(); navigate('/auth'); };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 glass" data-testid="main-header">
      <div className="pride-bar" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 gap-2">
          <Link to="/" className="flex items-center gap-1.5 shrink-0" data-testid="logo-link">
            <LotusLogo size={34} />
            <span className="font-heading text-base font-bold text-bloom-violet hidden sm:block">Bloom</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}
                  data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                  className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 whitespace-nowrap
                    ${isActive ? 'bg-bloom-violet/10 text-bloom-violet' : 'text-bloom-sub hover:text-bloom-violet hover:bg-bloom-violet/5'}`}
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  <span className="hidden xl:inline">{item.label}</span>
                </Link>
              );
            })}

            {/* T-World – všechno, co se váže k místu, pod jednou položkou. */}
            {tworldItems.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" data-testid="nav-tworld"
                    className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 whitespace-nowrap
                      ${vTworld ? 'bg-bloom-violet/10 text-bloom-violet' : 'text-bloom-sub hover:text-bloom-violet hover:bg-bloom-violet/5'}`}>
                    <Globe className="w-3.5 h-3.5 shrink-0" />
                    <span className="hidden xl:inline">T-World</span>
                    <ChevronDown className="w-3 h-3 shrink-0 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-60">
                  {tworldItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem key={item.path} asChild>
                        <Link to={item.path} data-testid={`tworld-${item.path.replace('/', '')}`}
                              className="flex items-center gap-2.5 cursor-pointer">
                          <Icon className="w-4 h-4 text-bloom-violet shrink-0" />
                          {item.label}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>

          <div className="flex items-center gap-2">
            {/* Global search — icon button (desktop + mobile) */}
            <div className="relative" ref={searchRef}>
              <button
                className="p-2 rounded-lg hover:bg-bloom-violet/5 transition-colors text-bloom-sub hover:text-bloom-violet"
                onClick={() => { setMobileMenuOpen(false); setSearchOpen(o => !o); if (!searchOpen) setTimeout(() => document.getElementById('global-search-input')?.focus(), 50); }}
                data-testid="search-icon-btn"
                aria-label={t('nav.hledat')}
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Search dropdown panel */}
              {searchOpen && (
                <div className="fixed sm:absolute inset-x-2 sm:inset-x-auto sm:right-0 sm:left-auto top-[59px] sm:top-full sm:mt-1 w-auto sm:w-80 bg-white border border-border rounded-xl shadow-lg z-50 p-3 space-y-2" data-testid="search-panel">
                  <form onSubmit={handleSearchSubmit}>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub/50 pointer-events-none" />
                      <input
                        id="global-search-input"
                        value={searchQuery}
                        onChange={e => handleSearchChange(e.target.value)}
                        placeholder={t('nav.hledatPlaceholder')}
                        className="w-full pl-9 pr-3 py-2 text-base md:text-sm bg-bloom-violet/5 border border-bloom-violet/20 rounded-lg outline-none focus:ring-1 focus:ring-bloom-violet/40 text-bloom-text placeholder:text-bloom-sub/60"
                        data-testid="global-search-input"
                      />
                    </div>
                  </form>

                  {/* Autocomplete suggestions */}
                  {hasSuggestions && (
                    <div className="space-y-1" data-testid="search-suggestions">
                      {Object.entries(suggestions).map(([type, items]) => {
                        if (!items?.length) return null;
                        const Icon = TYPE_ICONS[type];
                        return (
                          <div key={type}>
                            <p className="text-[10px] font-semibold text-bloom-sub/60 uppercase tracking-wider px-1 py-0.5">{TYPE_KEYS.includes(type) ? t(`typ.${type}`) : type}</p>
                            {items.map(item => (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => { navigate(getSuggestionLink(type, item)); setSearchOpen(false); setSearchQuery(''); setSuggestions(null); }}
                                className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-bloom-violet/5 transition-colors text-left"
                                data-testid={`suggestion-${type}-${item.id}`}
                              >
                                <Icon className="w-3.5 h-3.5 text-bloom-sub/60 shrink-0" />
                                <span className="text-sm text-bloom-text line-clamp-1">{getSuggestionTitle(type, item)}</span>
                              </button>
                            ))}
                          </div>
                        );
                      })}
                      <div className="border-t border-border/50 pt-1">
                        <button type="button" onClick={handleSearchSubmit} className="w-full text-xs text-bloom-violet hover:bg-bloom-violet/5 px-2 py-1.5 rounded-lg text-left flex items-center gap-1.5" data-testid="search-all-btn">
                          <Search className="w-3 h-3" />{t('nav.vsechnyVysledky', { dotaz: searchQuery })}
                        </button>
                      </div>
                    </div>
                  )}

                  {searchQuery.length >= 2 && !hasSuggestions && (
                    <p className="text-xs text-bloom-sub text-center py-1">Nic nenalezeno</p>
                  )}
                </div>
              )}
            </div>

            {/* Jen na stránkách Bloom World – jinde by přepnutí nic neudělalo. */}
            <LanguageSwitch />

            <Link to="/messages" className="relative p-2 rounded-lg hover:bg-bloom-violet/5 transition-colors" data-testid="messages-link">
              <MessageCircle className="w-5 h-5 text-bloom-sub" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-bloom-violet text-white text-[10px] font-bold flex items-center justify-center" data-testid="unread-badge">
                  {unreadCount}
                </span>
              )}
            </Link>


            <DropdownMenu onOpenChange={(open) => { if (open) setMobileMenuOpen(false); }}>
              <DropdownMenuTrigger asChild>
                <button className="relative flex items-center gap-2 p-1 rounded-lg hover:bg-bloom-violet/5 transition-colors" data-testid="user-menu-trigger">
                  <Avatar className="w-8 h-8 border-2 border-bloom-violet/20">
                    <AvatarImage src={getAvatarImage(user?.avatar, user?.custom_avatar)} alt={user?.username} />
                    <AvatarFallback className="bg-bloom-violet text-white text-sm">{user?.username?.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  {user?.email_verified === false && (
                    <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-amber-500 ring-2 ring-white animate-pulse" title={t('nav.emailNeoveren')} data-testid="avatar-unverified-dot" />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-semibold text-bloom-text">{user?.username}</p>
                  <p className="text-xs text-bloom-sub">{user?.pronouns}</p>
                  {user?.location && <p className="text-xs text-bloom-violet flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{user.location}</p>}
                </div>
                {user?.email_verified === false && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild className="bg-amber-50 border-y border-amber-200/60 focus:bg-amber-100 focus:text-amber-900">
                      <Link to="/profile" className="flex items-center gap-2 text-amber-700 font-medium cursor-pointer" data-testid="unverified-profile-link">
                        {t('nav.neovereny')}
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link to="/profile" className="flex items-center gap-2" data-testid="profile-link"><User className="w-4 h-4" />{t('nav.profil')}</Link></DropdownMenuItem>
                {isAdmin && <DropdownMenuItem asChild><Link to="/admin" className="flex items-center gap-2" data-testid="admin-link"><Shield className="w-4 h-4" />{t('nav.administrace')}</Link></DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive" data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2" />{t('nav.odhlasit')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button ref={mobileToggleRef} className="lg:hidden p-2 rounded-lg hover:bg-bloom-violet/5" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-5 h-5 text-bloom-text" /> : <Menu className="w-5 h-5 text-bloom-text" />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div ref={mobileMenuRef} className="lg:hidden bg-white border-t border-border">
          <nav className="px-4 py-3 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all
                    ${isActive ? 'bg-bloom-violet/10 text-bloom-violet font-medium' : 'text-bloom-sub hover:bg-bloom-violet/5'}`}>
                  <Icon className="w-5 h-5" />{item.label}
                </Link>
              );
            })}

            {/* Na mobilu se nerozbaluje – nabídka v nabídce se špatně trefí.
                Položky jsou rovnou pod sebou, jen odsazené pod nadpisem. */}
            {tworldItems.length > 0 && (
              <>
                <p className="px-4 pt-3 pb-1 text-xs font-semibold text-bloom-sub/70 uppercase tracking-wide flex items-center gap-2">
                  <Globe className="w-3.5 h-3.5" />T-World
                </p>
                {tworldItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 pl-8 pr-4 py-2.5 rounded-lg transition-all
                        ${isActive ? 'bg-bloom-violet/10 text-bloom-violet font-medium' : 'text-bloom-sub hover:bg-bloom-violet/5'}`}>
                      <Icon className="w-5 h-5" />{item.label}
                    </Link>
                  );
                })}
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};
